// Pure helper for reconciling a lane's role between the backend lane row and the
// BPMN diagram. Kept free of bpmn-js so it can be unit-tested in isolation.
//
// The BPMN diagram (XML) is the source of truth for a lane's role: it is what the
// user edits via the RoleSelector and what persists back through the process
// definition. The backend `lanes` list is a secondary view that can lag behind a
// just-made edit. So we only ever *hydrate* a lane that has no role yet (e.g. a
// legacy diagram whose XML never carried a roleId); once a lane has a roleId we
// leave it alone. This is what stops the flash-then-revert and the vanishing
// label: a stale/empty backend refetch can no longer overwrite an assigned role.

/** Minimal shape of a backend lane row. */
export interface BackendLane {
    name?: string | null;
    role_id?: string | null;
}

/** Minimal shape of the BPMN lane's businessObject. */
export interface LaneBusinessObject {
    name?: string | null;
    roleId?: string | null;
}

/** Properties to apply to the diagram lane, or null to leave it untouched. */
export interface LaneHydration {
    roleId: string;
    /** Only set when the backend has a non-empty name — never blank an existing label. */
    name?: string;
}

/**
 * Decide how (if at all) to hydrate a diagram lane from its backend row.
 * Returns null when the diagram should be left as-is:
 *   - the lane already has a role (diagram is authoritative), or
 *   - the backend has no role to hydrate from.
 */
export function hydrateLaneFromBackend(
    lane: BackendLane,
    businessObject: LaneBusinessObject
): LaneHydration | null {
    if (businessObject.roleId) return null; // already assigned → don't touch
    if (!lane.role_id) return null; // nothing to hydrate from

    const hydration: LaneHydration = { roleId: lane.role_id };
    const name = (lane.name ?? "").trim();
    if (name) hydration.name = lane.name as string;
    return hydration;
}

/** Minimal shape of a role we can match a lane against. */
export interface RoleMatch {
    identity: string;
    name?: string | null;
}

/**
 * Resolve which role a lane holds, given the roles available.
 *
 * The diagram is authoritative, so the user's just-picked `diagramRoleId` is
 * tried first; only then the backend `backendRoleId` (which can lag behind a
 * fresh edit), and finally a name match for a legacy lane that carries no id.
 * Preferring the diagram is what stops a stale backend value, sitting earlier in
 * `roles`, from winning the match and overwriting the choice the user just made.
 * Returns undefined when nothing resolves (e.g. a roleId from an imported diagram
 * that names a role this org does not have).
 */
export function matchLaneRole<T extends RoleMatch>(
    roles: readonly T[],
    diagramRoleId: string | null | undefined,
    backendRoleId: string | null | undefined,
    elementName: string | null | undefined
): T | undefined {
    return (
        (diagramRoleId
            ? roles.find((r) => r.identity === diagramRoleId)
            : undefined) ??
        (backendRoleId
            ? roles.find((r) => r.identity === backendRoleId)
            : undefined) ??
        (elementName ? roles.find((r) => r.name === elementName) : undefined)
    );
}

/**
 * Whether a lane counts as labelled for validation. A lane's identity is its
 * role, so a lane with a roleId is labelled even when its `name` attribute is
 * momentarily blank. The `label-required` bpmnlint rule (.bpmnlintrc.js) calls
 * this directly, so the shipped check and these unit tests can't drift.
 */
export function laneIsLabelled(businessObject: LaneBusinessObject): boolean {
    return Boolean((businessObject.name ?? "").trim() || businessObject.roleId);
}
